import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useIsSSO } from '~/hooks/useIsSSO';
import { workspaceService } from '../../services/workspaceAPI';
import { getWorkspaceMembersQueryKey } from './useWorkspaceMembersLazyQuery';
import { workspaceService as userWorkspaceService } from '../../services/userAPI';
import { getWorkspacesQueryKey } from './useWorkspacesQuery';
import { getOrganizationsQueryKey } from '../organization/useOrganizationsQuery';
import { formatErrorPayload, validateApiResponse } from '../utils';

export enum WorkspaceInvitesQueryErrors {
  GET_WORKSPACE_INVITES_ERROR = 'GET_WORKSPACE_INVITES_ERROR,',
  GET_WORKSPACE_INVITE_ERROR = 'GET_WORKSPACE_INVITE_ERROR',
  ADD_USER_TO_WORKSPACE_ERROR = 'ADD_USER_TO_WORKSPACE_ERROR',
  DELETE_WORKSPACE_INVITE_ERROR = 'DELETE_WORKSPACE_INVITE_ERROR',
  ACCEPT_WORKSPACE_INVITE_ERROR = 'ACCEPT_WORKSPACE_INVITE_ERROR',
}

export const getWorkspaceInvitesQueryKey = (workspaceId: string) => [
  'workspaceInvites',
  workspaceId,
];
export const getWorkspaceInviteQueryKey = (token?: string) => ['workspaceInvite', token];

export function useWorkspaceInvitesQuery(workspaceId: string) {
  const dispatch = useDispatch();
  const { realm } = useIsSSO();

  return useQuery(
    getWorkspaceInvitesQueryKey(workspaceId),
    async () => {
      const response = validateApiResponse(
        await workspaceService.getWorkspaceOutgoingInvites({ workspaceId, realmId: realm }),
      );
      return response.data;
    },
    {
      onError: (error: Error) =>
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceInvitesQueryErrors.GET_WORKSPACE_INVITES_ERROR,
            organizationId: workspaceId,
          }),
        ),
    },
  );
}

export function useWorkspaceInviteMutation(workspaceId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { realm } = useIsSSO();

  return useMutation(
    async ({ email }: { email: string }) => {
      const response = validateApiResponse(
        await workspaceService.addUserToWorkspace(workspaceId, { email, realmId: realm }),
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (!data) return;
        /** When adding user to workspace by email there might be situation where it is not known(on UI) whether the email
         * the user entered is that of an organization member. CDS checks the case and if it is, User type data is returned,
         * otherwise a WorkspaceInvitation type data is returned
         * */
        queryClient.invalidateQueries(getWorkspaceInvitesQueryKey(workspaceId));
        queryClient.invalidateQueries(getWorkspaceMembersQueryKey({ workspaceId }));
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceInvitesQueryErrors.ADD_USER_TO_WORKSPACE_ERROR,
          }),
        );
      },
    },
  );
}

export function useWorkspaceDeleteInviteMutation(workspaceId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ inviteId }: { inviteId: string }) => {
      const response = validateApiResponse(
        await workspaceService.deleteWorkspaceInvite(workspaceId, inviteId),
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (!data) return;
        queryClient.invalidateQueries(getWorkspaceInvitesQueryKey((data as any)?.workspace_id));
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceInvitesQueryErrors.DELETE_WORKSPACE_INVITE_ERROR,
          }),
        );
      },
    },
  );
}

export function useWorkspaceInviteQuery(token?: string) {
  const dispatch = useDispatch();
  return useQuery(
    getWorkspaceInviteQueryKey(token),
    async () => {
      if (!token) return undefined;
      const response = validateApiResponse(
        await userWorkspaceService.getWorkspaceInviteByToken(token),
      );
      return response.data;
    },
    {
      enabled: !!token,
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceInvitesQueryErrors.GET_WORKSPACE_INVITE_ERROR,
            showSnackbar: false,
          }),
        );
      },
    },
  );
}

export function useAcceptWorkspaceInviteMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ token }: { token: string }) => {
      validateApiResponse(await userWorkspaceService.acceptWorkspaceInvite(token));
      return token;
    },
    {
      onSuccess: (token) => {
        queryClient.removeQueries(getWorkspaceInviteQueryKey(token));
        queryClient.invalidateQueries(getOrganizationsQueryKey());
        queryClient.invalidateQueries(getWorkspacesQueryKey());
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceInvitesQueryErrors.ACCEPT_WORKSPACE_INVITE_ERROR,
          }),
        );
      },
    },
  );
}
